/**
 * Re-Authentication Modal Component
 * Allows users to log back in after session expiration
 * without losing their current work/page state
 * 
 * Supports:
 * - Email/password authentication
 * - Two-factor authentication (TOTP)
 * - Recovery code login
 * - Forced password change flow
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Checkbox, 
  Alert, 
  Space, 
  Typography,
  Divider 
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  LoginOutlined, 
  SafetyOutlined, 
  KeyOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined 
} from '@ant-design/icons';
import { useReAuth, ReAuthReason } from '../../context/ReAuthContext';
import { useAuth } from '../../context/AuthContext';
import authService, { forcedPasswordChange } from '../../services/authService';
import { TokenStorage, notifyReAuthSuccess } from '../../services/apiClient';
import styles from './ReAuthModal.module.css';

const { Text, Title } = Typography;

/**
 * Re-Authentication Modal
 * Displayed when session expires to allow seamless re-login
 */
const ReAuthModal = () => {
  const { t } = useTranslation();
  const { 
    showModal, 
    reason, 
    prefilledEmail, 
    onReAuthSuccess, 
    onReAuthCancel,
    onAccountDisabled,
    setIsReAuthenticating 
  } = useReAuth();
  const { updateUser, user } = useAuth();
  
  // Form instances
  const [form] = Form.useForm();
  const [twoFactorForm] = Form.useForm();
  const [passwordChangeForm] = Form.useForm();
  
  // Local state
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  
  // 2FA state
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryWarning, setRecoveryWarning] = useState(null);
  const [rememberMeForSession, setRememberMeForSession] = useState(false);
  
  // Password change required state
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [passwordChangeUserId, setPasswordChangeUserId] = useState(null);
  const [passwordChangeEmail, setPasswordChangeEmail] = useState(null);
  const [currentPasswordForChange, setCurrentPasswordForChange] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (showModal) {
      // Pre-fill email from user or cached value
      const email = prefilledEmail || user?.email || '';
      form.setFieldsValue({ email });
      setLoginError(null);
      setRequiresTwoFactor(false);
      setRequiresPasswordChange(false);
      setUseRecoveryCode(false);
      setRecoveryWarning(null);
    } else {
      // Reset forms when modal closes
      form.resetFields();
      twoFactorForm.resetFields();
      passwordChangeForm.resetFields();
    }
  }, [showModal, prefilledEmail, user?.email, form, twoFactorForm, passwordChangeForm]);

  /**
   * Get modal title based on current step
   */
  const getTitle = () => {
    if (requiresPasswordChange) {
      return t('auth.passwordChangeRequired', 'Password Change Required');
    }
    if (requiresTwoFactor) {
      return t('auth.twoFactorVerification', 'Two-Factor Verification');
    }
    return t('reAuth.title', 'Session Expired');
  };

  /**
   * Get message based on reason
   */
  const getReasonMessage = () => {
    switch (reason) {
      case ReAuthReason.SESSION_REVOKED:
        return t('reAuth.sessionRevokedMessage', 'Your session was ended. Please log in again to continue.');
      case ReAuthReason.ACCOUNT_DISABLED:
        return t('reAuth.accountDisabledMessage', 'Your account has been disabled. Please contact an administrator.');
      case ReAuthReason.TOKEN_INVALID:
        return t('reAuth.tokenInvalidMessage', 'Your session is invalid. Please log in again.');
      case ReAuthReason.SESSION_EXPIRED:
      default:
        return t('reAuth.sessionExpiredMessage', 'Your session has expired. Please log in again to continue where you left off.');
    }
  };

  /**
   * Handle email/password form submission
   */
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setIsReAuthenticating(true);
    setLoginError(null);

    try {
      const response = await authService.login(values.email, values.password, values.remember);
      
      // Handle 2FA required
      if (response.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setTwoFactorUserId(response.twoFactorUserId);
        setRememberMeForSession(values.remember);
        return;
      }
      
      // Handle forced password change
      if (response.passwordChangeRequired) {
        setRequiresPasswordChange(true);
        setPasswordChangeUserId(response.userId);
        setPasswordChangeEmail(response.email);
        setCurrentPasswordForChange(values.password);
        setRememberMeForSession(values.remember);
        return;
      }
      
      if (response.success) {
        // Update auth context with new user
        if (updateUser && response.user) {
          updateUser(response.user);
        }
        // Get new access token and notify apiClient to retry pending requests
        const accessToken = TokenStorage.getAccessToken();
        notifyReAuthSuccess(accessToken);
        onReAuthSuccess(accessToken);
      } else {
        setLoginError(response.message || t('auth.loginFailed', 'Login failed'));
      }
    } catch (err) {
      // Check if account is disabled
      if (err.message?.toLowerCase().includes('disabled')) {
        setLoginError(t('reAuth.accountDisabledMessage', 'Your account has been disabled.'));
        setTimeout(() => onAccountDisabled(), 2000);
        return;
      }
      setLoginError(err.message || t('auth.loginFailed', 'Login failed'));
    } finally {
      setSubmitting(false);
      setIsReAuthenticating(false);
    }
  };

  /**
   * Handle 2FA verification
   */
  const handleVerify2FA = async (values) => {
    setSubmitting(true);
    setIsReAuthenticating(true);
    setLoginError(null);

    try {
      const response = await authService.verify2FA(
        twoFactorUserId, 
        values.verificationCode, 
        values.rememberDevice, 
        rememberMeForSession
      );
      
      if (response.success) {
        if (updateUser && response.user) {
          updateUser(response.user);
        }
        const accessToken = TokenStorage.getAccessToken();
        notifyReAuthSuccess(accessToken);
        onReAuthSuccess(accessToken);
      } else {
        setLoginError(response.message || t('auth.invalidVerificationCode', 'Invalid verification code'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.verificationFailed', 'Verification failed'));
    } finally {
      setSubmitting(false);
      setIsReAuthenticating(false);
    }
  };

  /**
   * Handle recovery code login
   */
  const handleRecoveryLogin = async (values) => {
    setSubmitting(true);
    setIsReAuthenticating(true);
    setLoginError(null);
    setRecoveryWarning(null);

    try {
      const response = await authService.loginWithRecoveryCode(
        twoFactorUserId, 
        values.recoveryCode, 
        rememberMeForSession
      );
      
      if (response.success) {
        if (response.warning) {
          setRecoveryWarning(response.warning);
        }
        if (updateUser && response.user) {
          updateUser(response.user);
        }
        const accessToken = TokenStorage.getAccessToken();
        notifyReAuthSuccess(accessToken);
        onReAuthSuccess(accessToken);
      } else {
        setLoginError(response.message || t('auth.invalidRecoveryCode', 'Invalid recovery code'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.recoveryLoginFailed', 'Recovery login failed'));
    } finally {
      setSubmitting(false);
      setIsReAuthenticating(false);
    }
  };

  /**
   * Handle forced password change
   */
  const handleForcedPasswordChange = async (values) => {
    setSubmitting(true);
    setIsReAuthenticating(true);
    setLoginError(null);

    try {
      const result = await forcedPasswordChange(
        passwordChangeUserId,
        currentPasswordForChange,
        values.newPassword,
        values.confirmPassword
      );
      
      if (result.success) {
        if (updateUser && result.user) {
          updateUser(result.user);
        }
        const accessToken = TokenStorage.getAccessToken();
        notifyReAuthSuccess(accessToken);
        onReAuthSuccess(accessToken);
      } else {
        setLoginError(result.message || t('auth.passwordChangeFailed', 'Password change failed'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.passwordChangeFailed', 'Password change failed'));
    } finally {
      setSubmitting(false);
      setIsReAuthenticating(false);
    }
  };

  /**
   * Go back to email/password form
   */
  const handleBackToLogin = () => {
    setRequiresTwoFactor(false);
    setTwoFactorUserId(null);
    setUseRecoveryCode(false);
    setRequiresPasswordChange(false);
    setPasswordChangeUserId(null);
    setPasswordChangeEmail(null);
    setCurrentPasswordForChange(null);
    setRememberMeForSession(false);
    setLoginError(null);
    setRecoveryWarning(null);
    twoFactorForm.resetFields();
    passwordChangeForm.resetFields();
  };

  /**
   * Handle cancel - user wants to go to login page
   */
  const handleCancel = () => {
    onReAuthCancel();
  };

  // Don't render if modal isn't showing
  if (!showModal) {
    return null;
  }

  return (
    <Modal
      open={showModal}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={440}
      className={styles.modal}
      destroyOnClose
    >
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            {reason === ReAuthReason.ACCOUNT_DISABLED ? (
              <CloseCircleOutlined className={styles.iconDisabled} />
            ) : (
              <ExclamationCircleOutlined className={styles.iconWarning} />
            )}
          </div>
          <Title level={4} className={styles.title}>
            {getTitle()}
          </Title>
          <Text type="secondary" className={styles.subtitle}>
            {requiresPasswordChange
              ? t('auth.passwordChangeRequiredMessage', 'Your administrator requires you to change your password.')
              : requiresTwoFactor
                ? (useRecoveryCode 
                    ? t('auth.enterRecoveryCode', 'Enter your recovery code') 
                    : t('auth.enterVerificationCode', 'Enter the verification code from your authenticator app'))
                : getReasonMessage()
            }
          </Text>
        </div>

        {/* Error/Warning alerts */}
        {loginError && (
          <Alert
            message={loginError}
            type="error"
            showIcon
            closable
            onClose={() => setLoginError(null)}
            className={styles.alert}
          />
        )}

        {recoveryWarning && (
          <Alert
            message={recoveryWarning}
            type="warning"
            showIcon
            closable
            onClose={() => setRecoveryWarning(null)}
            className={styles.alert}
          />
        )}

        {/* Password Change Form */}
        {requiresPasswordChange ? (
          <Form
            form={passwordChangeForm}
            name="reauth-password-change"
            onFinish={handleForcedPasswordChange}
            layout="vertical"
            requiredMark={false}
            className={styles.form}
          >
            <Form.Item
              name="newPassword"
              label={t('auth.newPassword', 'New Password')}
              rules={[
                { required: true, message: t('auth.newPasswordRequired', 'New password is required') },
                { min: 12, message: t('auth.passwordMinLength', 'Password must be at least 12 characters') },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
                  message: t('auth.passwordComplexity', 'Must include uppercase, lowercase, number, and special character')
                }
              ]}
              extra={t('auth.passwordRequirements', 'At least 12 characters with uppercase, lowercase, number, and special character')}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.newPasswordPlaceholder', 'Enter new password')}
                size="large"
                autoComplete="new-password"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t('auth.confirmPassword', 'Confirm Password')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('auth.confirmPasswordRequired', 'Please confirm your password') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.passwordsMustMatch', 'Passwords do not match')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.confirmPasswordPlaceholder', 'Confirm new password')}
                size="large"
                autoComplete="new-password"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 24, marginBottom: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                icon={<LockOutlined />}
              >
                {submitting ? t('auth.changingPassword', 'Changing Password...') : t('auth.changePasswordAndLogin', 'Change Password & Continue')}
              </Button>
            </Form.Item>

            <Button
              type="link"
              onClick={handleBackToLogin}
              disabled={submitting}
              block
            >
              {t('auth.backToLogin', 'Back to Login')}
            </Button>
          </Form>
        ) : /* 2FA Form */
        requiresTwoFactor ? (
          <Form
            form={twoFactorForm}
            name="reauth-2fa"
            onFinish={useRecoveryCode ? handleRecoveryLogin : handleVerify2FA}
            layout="vertical"
            requiredMark={false}
            className={styles.form}
          >
            {useRecoveryCode ? (
              <Form.Item
                name="recoveryCode"
                label={t('auth.recoveryCode', 'Recovery Code')}
                rules={[
                  { required: true, message: t('auth.recoveryCodeRequired', 'Recovery code is required') },
                  { pattern: /^[A-Z0-9]{5}-[A-Z0-9]{5}$/i, message: t('auth.recoveryCodeFormat', 'Format: XXXXX-XXXXX') }
                ]}
                extra={t('auth.recoveryCodeHint', 'Enter one of your 10 recovery codes')}
              >
                <Input
                  prefix={<KeyOutlined className={styles.inputIcon} />}
                  placeholder="XXXXX-XXXXX"
                  size="large"
                  autoComplete="off"
                  disabled={submitting}
                  style={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item
                  name="verificationCode"
                  label={t('auth.verificationCode', 'Verification Code')}
                  rules={[
                    { required: true, message: t('auth.verificationCodeRequired', 'Verification code is required') },
                    { pattern: /^\d{6}$/, message: t('auth.verificationCodeFormat', 'Must be 6 digits') }
                  ]}
                  extra={t('auth.verificationCodeHint', 'Enter the 6-digit code from your authenticator app')}
                >
                  <Input
                    prefix={<SafetyOutlined className={styles.inputIcon} />}
                    placeholder="000000"
                    size="large"
                    maxLength={6}
                    autoComplete="one-time-code"
                    disabled={submitting}
                    style={{ fontFamily: 'monospace', letterSpacing: '0.3em', textAlign: 'center' }}
                  />
                </Form.Item>
                
                <Form.Item name="rememberDevice" valuePropName="checked" noStyle>
                  <Checkbox disabled={submitting}>
                    {t('auth.rememberDevice', 'Remember this device for 30 days')}
                  </Checkbox>
                </Form.Item>
              </>
            )}

            <Form.Item style={{ marginTop: 24, marginBottom: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                icon={<SafetyOutlined />}
              >
                {submitting ? t('auth.verifying', 'Verifying...') : t('auth.verify', 'Verify')}
              </Button>
            </Form.Item>

            <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
              <Button
                type="link"
                onClick={() => {
                  setUseRecoveryCode(!useRecoveryCode);
                  setLoginError(null);
                  twoFactorForm.resetFields();
                }}
                disabled={submitting}
              >
                {useRecoveryCode 
                  ? t('auth.useAuthenticatorInstead', 'Use authenticator app instead') 
                  : t('auth.useRecoveryCode', 'Use a recovery code')
                }
              </Button>
              
              <Button
                type="link"
                onClick={handleBackToLogin}
                disabled={submitting}
              >
                {t('auth.backToLogin', 'Back to Login')}
              </Button>
            </Space>
          </Form>
        ) : (
          /* Email/Password Form */
          <Form
            form={form}
            name="reauth-login"
            onFinish={handleSubmit}
            layout="vertical"
            requiredMark={false}
            className={styles.form}
          >
            <Form.Item
              name="email"
              label={t('auth.email', 'Email')}
              rules={[
                { required: true, message: t('auth.emailRequired', 'Email is required') },
                { type: 'email', message: t('auth.emailInvalid', 'Invalid email address') }
              ]}
            >
              <Input
                prefix={<UserOutlined className={styles.inputIcon} />}
                placeholder={t('auth.emailPlaceholder', 'Enter your email')}
                size="large"
                autoComplete="email"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('auth.password', 'Password')}
              rules={[
                { required: true, message: t('auth.passwordRequired', 'Password is required') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
                size="large"
                autoComplete="current-password"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox disabled={submitting}>
                {t('auth.rememberMe', 'Remember me')}
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginTop: 24, marginBottom: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                icon={<LoginOutlined />}
              >
                {submitting ? t('reAuth.loggingIn', 'Logging in...') : t('reAuth.continueWorking', 'Log In to Continue')}
              </Button>
            </Form.Item>
          </Form>
        )}

        <Divider className={styles.divider} />

        {/* Cancel option */}
        <div className={styles.cancelSection}>
          <Text type="secondary" className={styles.cancelText}>
            {t('reAuth.orGoToLogin', 'Or return to the login page')}
          </Text>
          <Button 
            type="link" 
            onClick={handleCancel}
            disabled={submitting}
            className={styles.cancelButton}
          >
            {t('reAuth.goToLoginPage', 'Go to Login Page')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReAuthModal;
